import { GluestackUIProvider } from '@vidahealth/vida-ui';
import { colorScheme as nativewindColorScheme, vars } from 'nativewind';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Platform, useColorScheme, View } from 'react-native';

import { onboardingTheme } from '../onboardingTheme';

type OnboardingThemeProviderProps = {
  children: ReactNode;
};

type ColorMode = 'light' | 'dark';

const getWebColorMode = (): ColorMode => {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }

  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
};

/**
 * Vida's theme, mounted. **This is ours to mount, not a host's to supply.**
 *
 * Our published components are styled entirely through nativewind `className`s that reference
 * CSS variables — `--color-*` — which only exist below a `View` carrying `vars(…)`. That is a
 * *tree-position* requirement, not an instance-identity one: the variables cascade down the
 * view tree through nativewind (a shared peer), so it does not matter whose copy of vida-ui
 * stamped them. Which is why `@vidahealth/vida-ui` is a plain `dependency` and a second copy in
 * a consumer's tree costs bundle size and nothing else.
 *
 * It also means the split we used to ship was the wrong one: we installed vida-ui ourselves and
 * then asked the *host* to import it and mount `GluestackUIProvider` above our components. A
 * host cannot reasonably be asked to satisfy a requirement of a library it did not know it had,
 * and forgetting it fails silently — `ThemeContext` has a default, so nothing throws; the
 * screens just render with no palette. Shipping this component as `./theme` replaces that with
 * one import from us, and the host never touches vida-ui. See `docs/packaging-plan.md` §9.2.
 *
 * **Mount it once, at a root.** Not inside a feature provider — that was tried and reverted
 * (§9.3): `GluestackUIProvider` hardcodes `h-full w-full flex-1` on its wrapping `View`, which
 * is right at a root and wrong partway down a host's screen; it calls nativewind's
 * `setColorScheme`, which **throws** without `darkMode: 'class'`, so it cannot be mounted
 * defensively; and two assessments on one screen would nest two of them.
 *
 * **`@react-navigation/native` is deliberately absent.** It used to wrap children here, as
 * `AppThemeProvider`, and it is a `devDependency` — so this module could not have been exported
 * at all without a consumer hitting `Cannot find module` on import, Metro not being a
 * tree-shaker. Navigation chrome is the app's, and now lives in `app/_layout.tsx` as a local
 * `AppThemeProvider` that composes this with a `NavigationThemeProvider`. That reads nativewind's
 * colour scheme, which this provider is the thing that sets — so nothing has to be threaded
 * out of here to reach it.
 */
export function OnboardingThemeProvider({ children }: OnboardingThemeProviderProps) {
  const scheme = useColorScheme();
  const nativeMode = scheme === 'dark' ? 'dark' : 'light';
  const [mode, setMode] = useState<ColorMode>(() => {
    if (Platform.OS === 'web') return getWebColorMode();
    return nativeMode;
  });

  const themeVars = useMemo(() => vars(onboardingTheme.modes[mode].cssVariables), [mode]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      setMode(nativeMode);
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');

    // Detect via the media query, then push the result into nativewind's color
    // scheme. vida-ui's GluestackUIProvider reads nativewind's `useColorScheme()`
    // to choose the CSS variables it stamps on the `display:contents` wrapper
    // that hosts gluestack portals (Actionsheet/BottomSheet/Select). The dev
    // runtime tracks the system automatically, but the production web export
    // leaves nativewind at 'light' unless we set it — which made portals render
    // the light palette over a dark page (e.g. the existing-account sheet).
    // (class = how dark mode is applied; media = how we detect it.)
    const syncWebMode = () => {
      const next: ColorMode = media.matches ? 'dark' : 'light';
      setMode(next);
      try {
        nativewindColorScheme.set(next);
      } catch {
        // NativeWind throws "Cannot manually set color scheme" under
        // `darkMode: 'media'`, where the media query already owns the scheme — so the
        // value we just derived from `matchMedia` is the one it is using anyway, and
        // there is nothing to push. Only `'class'` needs this call, and only because
        // under `'class'` nothing else moves the scheme.
        //
        // Guarded rather than skipped: a host picks its own `darkMode` strategy, and
        // an unhandled throw here takes their app down at mount for a no-op.
      }
    };

    syncWebMode();
    media.addEventListener('change', syncWebMode);

    return () => media.removeEventListener('change', syncWebMode);
  }, [nativeMode]);

  return (
    <GluestackUIProvider mode='system' theme={onboardingTheme}>
      <View className='h-full w-full flex-1' style={themeVars}>
        {children}
      </View>
    </GluestackUIProvider>
  );
}
